import { Children, forwardRef, isValidElement, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ReactNode } from 'react';
import { ChevronsUpDown, Check, Search } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Opt { value: string; label: string; disabled?: boolean }

function textOf(node: ReactNode): string {
  if (node == null || node === false) return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(textOf).join('');
  if (isValidElement(node)) return textOf((node.props as { children?: ReactNode }).children);
  return '';
}

function collect(children: ReactNode, out: Opt[] = []): Opt[] {
  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;
    if (child.type === 'option') {
      const p = child.props as { value?: unknown; children?: ReactNode; disabled?: boolean };
      out.push({ value: String(p.value ?? ''), label: textOf(p.children), disabled: !!p.disabled });
    } else if ((child.props as { children?: ReactNode })?.children) {
      collect((child.props as { children?: ReactNode }).children, out);
    }
  });
  return out;
}

/**
 * Custom searchable dropdown that drop-in replaces a native <select>: keeps `{...register}` +
 * <option> children (via a hidden native select) and renders the menu in a PORTAL so it never gets
 * clipped by table/card overflow.
 */
export const Select = forwardRef<
  HTMLSelectElement,
  React.SelectHTMLAttributes<HTMLSelectElement> & { error?: boolean; compact?: boolean }
>(
  ({ className, children, disabled, onChange, error, compact, ...props }, ref) => {
    const innerRef = useRef<HTMLSelectElement | null>(null);
    const btnRef = useRef<HTMLButtonElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [current, setCurrent] = useState('');
    const [rect, setRect] = useState<{ top: number; left: number; width: number; maxH: number } | null>(null);

    const options = useMemo(() => collect(children), [children]);
    const placeholder = options.find((o) => o.value === '')?.label ?? 'Select…';
    const filtered = useMemo(() => {
      const q = query.trim().toLowerCase();
      return q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;
    }, [options, query]);
    const selectedLabel = options.find((o) => o.value === current)?.label;

    function setRef(el: HTMLSelectElement | null) {
      innerRef.current = el;
      if (typeof ref === 'function') ref(el);
      else if (ref) (ref as React.MutableRefObject<HTMLSelectElement | null>).current = el;
    }

    useEffect(() => {
      const el = innerRef.current;
      if (el && el.value !== current) setCurrent(el.value);
    });

    function place() {
      const b = btnRef.current?.getBoundingClientRect();
      if (!b) return;
      const below = window.innerHeight - b.bottom;
      const above = b.top;
      const wantH = 300;
      const openUp = below < Math.min(wantH, 220) && above > below;
      const maxH = Math.max(140, (openUp ? above : below) - 12);
      setRect({ top: openUp ? Math.max(8, b.top - Math.min(wantH, maxH) - 4) : b.bottom + 4, left: b.left, width: b.width, maxH });
    }
    useLayoutEffect(() => { if (open) place(); }, [open]);
    useEffect(() => {
      if (!open) { setQuery(''); return; }
      setTimeout(() => searchRef.current?.focus(), 0);
      function onDoc(e: MouseEvent) {
        const t = e.target as Node;
        if (!btnRef.current?.contains(t) && !panelRef.current?.contains(t)) setOpen(false);
      }
      function reposition(e?: Event) {
        // Don't close when the scroll happens inside the dropdown's own list.
        if (e && e.target instanceof Node && panelRef.current?.contains(e.target)) return;
        setOpen(false);
      }
      document.addEventListener('mousedown', onDoc);
      window.addEventListener('scroll', reposition, true);
      window.addEventListener('resize', reposition);
      return () => { document.removeEventListener('mousedown', onDoc); window.removeEventListener('scroll', reposition, true); window.removeEventListener('resize', reposition); };
    }, [open]);

    function pick(v: string) {
      const el = innerRef.current;
      if (el) { el.value = v; onChange?.({ target: el, currentTarget: el } as unknown as React.ChangeEvent<HTMLSelectElement>); }
      setCurrent(v);
      setOpen(false);
    }

    return (
      <div className={cn('relative', className)}>
        <select ref={setRef} onChange={onChange} disabled={disabled} tabIndex={-1} aria-hidden className="sr-only" {...props}>
          {children}
        </select>

        {/*
          Styled with the same `.input` class as every text box and date
          field, not a shape of its own. A dropdown that is a different
          height, radius and border from the field beside it is the thing
          that made these look like the browser's rather than the product's.
        */}
        <button
          ref={btnRef}
          type="button"
          disabled={disabled}
          onClick={() => setOpen((o) => !o)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(true); }
          }}
          aria-haspopup="listbox"
          aria-expanded={open}
          className={cn(
            'input flex items-center justify-between pr-2.5 text-left',
            // Pagination and toolbar dropdowns sit in a row of small
            // controls; a full-height field there would tower over them.
            compact && 'h-8 w-auto px-2 text-[13px]',
            error && 'input-error',
          )}
        >
          <span className={cn('truncate', !selectedLabel && 'text-ink-400 dark:text-ink-500')}>{selectedLabel || placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 text-ink-400" />
        </button>

        {open && rect && createPortal(
          <div ref={panelRef} style={{ position: 'fixed', top: rect.top, left: rect.left, width: rect.width, maxHeight: rect.maxH, zIndex: 9999 }}
            className="flex flex-col overflow-hidden rounded-md border border-border bg-card shadow-lg">
            {options.length > 6 && (
              <div className="flex items-center gap-2 border-b border-border px-2.5">
                <Search className="h-3.5 w-3.5 text-muted-foreground" />
                <input ref={searchRef} value={query} onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); else if (e.key === 'Enter') { e.preventDefault(); if (filtered[0]) pick(filtered[0].value); } }}
                  placeholder="Search…" className="h-10 w-full bg-transparent text-[15px] outline-none" />
              </div>
            )}
            <ul className="flex-1 overflow-y-auto py-1">
              {filtered.length === 0 ? (
                <li className="px-3 py-2 text-sm text-muted-foreground">No results.</li>
              ) : filtered.map((o) => (
                <li key={o.value + o.label}>
                  <button type="button" disabled={o.disabled} onClick={() => pick(o.value)}
                    className={cn('flex w-full items-center justify-between px-3 py-2.5 text-left text-[15px] hover:bg-accent', o.disabled && 'opacity-50')}>
                    <span className={cn('truncate', o.value === '' && 'text-muted-foreground')}>{o.label}</span>
                    {o.value === current && <Check className="h-4 w-4 text-primary" />}
                  </button>
                </li>
              ))}
            </ul>
          </div>,
          document.body,
        )}
      </div>
    );
  },
);
Select.displayName = 'Select';
